import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams, Link } from 'react-router-dom';
import { FiMail, FiUsers, FiClock, FiAlertCircle } from 'react-icons/fi';
import { organizationAPI, type InvitationDetails } from '../../api/organizations';
import { authAPI } from '../../api/auth';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { Button } from '../../components/ui/Button/Button';
import { Card } from '../../components/ui/Card/Card';
import { Badge } from '../../components/ui/Badge/Badge';
import styles from './AcceptInvitation.module.css';

export default function AcceptInvitation() {
  const [searchParams] = useSearchParams();
  const params = useParams<{ token?: string }>();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { switchOrganization } = useOrganization();

  // Support both /invite/:token path param and /accept-invitation?token= query param
  const token = params.token || searchParams.get('token');
  
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [acceptedOrganizationId, setAcceptedOrganizationId] = useState<string | null>(null);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [registering, setRegistering] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Registration form state
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setAcceptedOrganizationId(null);
    setAcceptError(null);
    if (token) {
      loadInvitation();
    } else {
      setError('Invalid invitation link');
      setLoading(false);
    }
  }, [token]);

  const loadInvitation = async () => {
    if (!token) return;

    try {
      setLoading(true);
      setError(null);
      const data = await organizationAPI.getInvitationDetails(token);
      setInvitation(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load invitation details');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async () => {
    if (!token) return;

    try {
      setAccepting(true);
      setAcceptError(null);
      let orgId = acceptedOrganizationId;
      if (!orgId) {
        const response = await organizationAPI.acceptInvitation(token);
        orgId = response.organization.id;
        setAcceptedOrganizationId(orgId);
      }

      // Acceptance adds membership; switching updates the session and selection.
      // Retain orgId so a failed switch can be retried without accepting twice.
      await switchOrganization(orgId, { reload: false });
      navigate('/dashboard');
      window.location.reload();
    } catch (err: any) {
      setAcceptError(err.message || 'Unable to open the organization. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    // Validation
    const errors: Record<string, string> = {};
    if (!firstName.trim()) errors.firstName = 'First name is required';
    if (!lastName.trim()) errors.lastName = 'Last name is required';
    if (!password || password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setRegistering(true);
      const response = await organizationAPI.registerWithInvitation(token, {
        password,
        first_name: firstName,
        last_name: lastName,
      });
      
      // Store tokens
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      
      // Redirect to dashboard
      navigate('/dashboard');
      window.location.reload(); // Reload to update auth context
    } catch (err: any) {
      setError(err.message || 'Failed to create account');
    } finally {
      setRegistering(false);
    }
  };

  const handleGoogleRegister = async () => {
    if (!token) return;

    try {
      setGoogleLoading(true);

      // Store the invitation token in sessionStorage so we can use it after Google callback
      sessionStorage.setItem('invitation_token', token);

      // Get Google OAuth URL and redirect
      const { url } = await authAPI.getGoogleAuthUrl();

      // Modify the redirect URI to include invitation context
      // The callback will check for invitation_token in sessionStorage
      window.location.href = url;
    } catch (err: any) {
      sessionStorage.removeItem('invitation_token');
      setError(err.message || 'Failed to start Google sign-up');
      setGoogleLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getRoleBadgeVariant = (role: string): 'primary' | 'secondary' | 'info' | 'tertiary' => {
    switch (role) {
      case 'admin': return 'info';
      case 'member': return 'secondary';
      case 'viewer': return 'tertiary';
      default: return 'secondary';
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading invitation...</div>
      </div>
    );
  }

  if (error || !invitation) {
    return (
      <div className={styles.container}>
        <Card className={styles.errorCard}>
          <FiAlertCircle className={styles.errorIcon} />
          <h2>Invalid Invitation</h2>
          <p>{error || 'This invitation link is invalid or has expired.'}</p>
          <Link to="/login">
            <Button variant="primary">Go to Login</Button>
          </Link>
        </Card>
      </div>
    );
  }

  if (!invitation.valid) {
    const reason =
      invitation.status === 'expired'
        ? 'This invitation has expired. Ask an administrator to send a new one.'
        : invitation.status === 'revoked'
        ? 'This invitation was revoked. Ask an administrator to send a new one.'
        : invitation.status === 'declined'
        ? 'This invitation was declined. Ask an administrator to send a new one.'
        : invitation.status === 'accepted'
        ? 'This invitation has already been used.'
        : 'This invitation is no longer valid.';

    return (
      <div className={styles.container}>
        <Card className={styles.errorCard}>
          <FiAlertCircle className={styles.errorIcon} />
          <h2>Invitation unavailable</h2>
          <p>{reason}</p>
          <Link to="/login">
            <Button variant="primary">Go to Login</Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Card className={styles.invitationCard}>
        <div className={styles.header}>
          <FiMail className={styles.headerIcon} />
          <h1>You've Been Invited!</h1>
        </div>

        <div className={styles.invitationDetails}>
          <div className={styles.detailRow}>
            <FiUsers className={styles.detailIcon} />
            <div>
              <p className={styles.detailLabel}>Organization</p>
              <p className={styles.detailValue}>{invitation.organization.name}</p>
            </div>
          </div>

          <div className={styles.detailRow}>
            <Badge variant={getRoleBadgeVariant(invitation.role)} size="md">
              {invitation.role}
            </Badge>
            <div>
              <p className={styles.detailLabel}>Role</p>
              <p className={styles.detailValue}>You'll join as a {invitation.role}</p>
            </div>
          </div>

          <div className={styles.detailRow}>
            <FiClock className={styles.detailIcon} />
            <div>
              <p className={styles.detailLabel}>Invited by</p>
              <p className={styles.detailValue}>{`${invitation.invited_by.first_name} ${invitation.invited_by.last_name}`.trim()}</p>
            </div>
          </div>

          <div className={styles.expiryNotice}>
            <FiClock className={styles.clockIcon} />
            <span>This invitation expires on {formatDate(invitation.expires_at)}</span>
          </div>
        </div>

        {user ? (
          // Existing user - show accept button
          <div className={styles.actions}>
            <p className={styles.loggedInAs}>
              Logged in as <strong>{user.email}</strong>
            </p>
            {acceptError && <p role="alert" className={styles.error}>{acceptError}</p>}
            <Button
              variant="primary"
              onClick={handleAcceptInvitation}
              disabled={accepting}
              className={styles.acceptButton}
            >
              {accepting ? 'Opening...' : acceptedOrganizationId ? 'Open organization' : 'Accept Invitation'}
            </Button>
            <p className={styles.switchAccount}>
              Not you?{' '}
              <button
                type="button"
                className={styles.linkButton}
                onClick={async () => {
                  await logout();
                  navigate(`/login`, { state: { from: { pathname: `/accept-invitation?token=${token}` } } });
                }}
              >
                Switch account
              </button>
            </p>
          </div>
        ) : (
          // New user - show registration form
          <div className={styles.registrationSection}>
            <h2>Create Your Account</h2>
            <p className={styles.subtitle}>
              Complete the form below to join {invitation.organization.name}
            </p>

            <form onSubmit={handleRegister} className={styles.form}>
              <div className={styles.formGroup}>
                <label>Email</label>
                <input
                  type="email"
                  value={invitation.email}
                  disabled
                  className={styles.input}
                />
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>First Name *</label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => {
                      setFirstName(e.target.value);
                      setFormErrors({ ...formErrors, firstName: '' });
                    }}
                    className={`${styles.input} ${formErrors.firstName ? styles.inputError : ''}`}
                    disabled={registering}
                  />
                  {formErrors.firstName && (
                    <p className={styles.error}>{formErrors.firstName}</p>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label>Last Name *</label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => {
                      setLastName(e.target.value);
                      setFormErrors({ ...formErrors, lastName: '' });
                    }}
                    className={`${styles.input} ${formErrors.lastName ? styles.inputError : ''}`}
                    disabled={registering}
                  />
                  {formErrors.lastName && (
                    <p className={styles.error}>{formErrors.lastName}</p>
                  )}
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>Password *</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setFormErrors({ ...formErrors, password: '' });
                  }}
                  className={`${styles.input} ${formErrors.password ? styles.inputError : ''}`}
                  placeholder="At least 8 characters"
                  disabled={registering}
                />
                {formErrors.password && (
                  <p className={styles.error}>{formErrors.password}</p>
                )}
              </div>

              <Button
                type="submit"
                variant="primary"
                disabled={registering || googleLoading}
                className={styles.submitButton}
              >
                {registering ? 'Creating Account...' : 'Create Account & Join'}
              </Button>
            </form>

            <div className={styles.divider}>
              <span>or</span>
            </div>

            <button
              type="button"
              className={styles.googleButton}
              onClick={handleGoogleRegister}
              disabled={registering || googleLoading}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" className={styles.googleIcon}>
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {googleLoading ? 'Connecting...' : 'Continue with Google'}
            </button>

            <p className={styles.googleNote}>
              Your Google account email must match: <strong>{invitation.email}</strong>
            </p>

            <p className={styles.loginLink}>
              Already have an account?{' '}
              <Link
                to="/login"
                state={{ from: { pathname: `/accept-invitation?token=${token}` } }}
              >
                Sign in
              </Link>
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}


